import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ShieldCheck, Stethoscope, ClipboardList, Sparkles, ArrowRight, CheckCircle2, User, ScanLine, Building2, Users, Send } from 'lucide-react';
import { api } from '@/lib/api';
import { useApp } from '@/context/AppContext';
import { useDemoLaunch } from '@/components/DemoAccessGate';
import { LEGAL_ENTITY } from '@/pages/legal/LegalLayout';
// Self-hosted: the app's CSP is `img-src 'self' data: blob:`, so any remote image
// silently fails to render. Assets live in the repo and ship with the bundle.
import heroImage from '@/assets/hero-clinical-staff.jpg';

// Whole dollars stay whole ($249), anything else keeps its cents ($124.50).
const usd = (n) => `$${Number.isInteger(n) ? n : n.toFixed(2)}`;

// Prices come from the API (`/pricing` reads plans.py + pricing_config.py,
// `/patient-requests/config` the patient-direct fee) - never typed in here, so
// a price change can't leave the site quoting an old number. Until they land,
// or if the API is down, each card explains the structure without a figure.
function usePricing() {
  const [prices, setPrices] = useState(null);
  const [direct, setDirect] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get('/pricing').then((r) => alive && setPrices(r.data)).catch(() => {});
    api.get('/patient-requests/config')
      .then((r) => alive && setDirect(r.data))
      .catch(() => alive && setDirect({ enabled: false }));
    return () => { alive = false; };
  }, []);
  return { prices, direct };
}

function PricingCard({ icon: I, tag, title, who, children, testid }) {
  return (
    <Card className="border bg-white flex flex-col" data-testid={testid}>
      <CardHeader className="pb-2">
        <div className="text-sm font-medium text-primary">{tag}</div>
        <CardTitle className="flex items-start gap-2 text-xl leading-snug"><I className="h-5 w-5 text-primary shrink-0 mt-1" /> {title}</CardTitle>
        <p className="text-sm text-muted-foreground">{who}</p>
      </CardHeader>
      <CardContent className="pt-0 flex-1 flex flex-col gap-3 text-base">{children}</CardContent>
    </Card>
  );
}


export default function Landing() {
  const { user } = useApp();
  const navigate = useNavigate();
  const { launch, dialog, busyRole: demoBusy, demoMode } = useDemoLaunch();

  const goAs = (role, path) => launch(role, () => navigate(path));
  const { prices, direct } = usePricing();
  const mode1 = prices?.patient_pays;
  const planC = prices?.practice_pays;
  const ladder = planC?.ladder || [];
  const directOn = !!direct?.enabled;

  return (
    <div className="relative min-h-screen hero-wash">
      {dialog}
      <div className="noise relative">
        <header className="sticky top-0 z-30 bg-background/70 backdrop-blur border-b">
          <div className="max-w-7xl mx-auto flex h-14 items-center px-4 lg:px-6 gap-3">
            <Link to="/" className="flex items-center gap-2" data-testid="landing-logo">
              <div className="h-7 w-7 rounded-lg bg-primary text-primary-foreground grid place-items-center font-bold">M</div>
              <span className="font-semibold tracking-tight">DoctorPaperwork</span>
            </Link>
            <div className="flex-1" />
            {user ? (
              <Button size="sm" onClick={() => navigate(user.role === 'doctor' ? '/doctor' : user.role === 'nurse' ? '/nurse' : '/admin')} data-testid="landing-open-dashboard">Open dashboard</Button>
            ) : (
              <>
                <Button size="sm" variant="ghost" onClick={() => navigate('/login')} data-testid="landing-signin-button">Sign in</Button>
                <Button size="sm" onClick={() => navigate('/signup')} data-testid="landing-getstarted-button">Get started</Button>
              </>
            )}
          </div>
        </header>

        <section className="max-w-6xl mx-auto px-4 lg:px-6 pt-12 pb-20 grid lg:grid-cols-2 gap-10 items-center">
          <div className="animate-in fade-in slide-in-from-bottom-3 duration-500">
            <div className="inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-3 py-1 text-sm"><Sparkles className="h-3.5 w-3.5" /> Physician-managed recovery recommendations</div>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-foreground">
              FMLA, STD, LTD & ADA — the <span className="text-primary">expedited</span> lane.
            </h1>
            <p className="mt-4 text-lg md:text-xl text-muted-foreground max-w-xl">
              The all-in-one medical-leave paperwork platform for practices. Take the staff and physician time each patient's forms eat today and replace it with a guided workflow, recovery recommendations grounded in your own procedure catalog, and one-tap signed delivery.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button size="lg" onClick={() => navigate('/signup')} data-testid="landing-cta-signup">
                Create your practice workspace <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
              {demoMode && (
                <Button size="lg" variant="outline" onClick={() => window.open('/intake/demo-practice-001?source=landing_demo', '_blank', 'noopener')} data-testid="landing-cta-patient-demo">
                  <User className="h-4 w-4 mr-1" /> Try as a Patient (demo)
                </Button>
              )}
            </div>
            {demoMode && (
            <div className="mt-3 flex flex-wrap gap-2 items-center" data-testid="landing-demo-row">
              <span className="text-sm text-muted-foreground mr-1">Explore the staff demo:</span>
              <Button size="sm" variant="outline" disabled={!!demoBusy} onClick={() => goAs('admin', '/admin')} data-testid="landing-cta-admin">
                {demoBusy === 'admin' ? 'Opening…' : 'Practice Admin'}
              </Button>
              <Button size="sm" variant="outline" disabled={!!demoBusy} onClick={() => goAs('nurse', '/nurse')} data-testid="landing-cta-nurse">
                {demoBusy === 'nurse' ? 'Opening…' : 'Staff Queue'}
              </Button>
              <Button size="sm" variant="outline" disabled={!!demoBusy} onClick={() => goAs('doctor', '/doctor')} data-testid="landing-cta-doctor">
                {demoBusy === 'doctor' ? 'Opening…' : 'Doctor Review'}
              </Button>
            </div>
            )}
            <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5 text-primary" /> HIPAA-aligned design</span>
              <span className="inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5 text-primary" /> Audit logs everywhere</span>
              <span className="inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5 text-primary" /> Tailored &amp; managed by your physician</span>
            </div>
          </div>
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 delay-100 fill-mode-backwards order-first lg:order-none">
            <div className="rounded-2xl border bg-card shadow-sm overflow-hidden">
              <img
                src={heroImage}
                alt="Clinical staff reviewing patient documentation"
                className="w-full h-72 lg:h-96 object-cover"
                width="1600"
                height="1065"
              />
              <div className="p-4 grid grid-cols-3 gap-3 text-sm">
                <div className="rounded-lg bg-secondary p-3"><div className="text-muted-foreground">Avg. completion</div><div className="text-lg font-semibold">~6 min</div></div>
                <div className="rounded-lg bg-secondary p-3"><div className="text-muted-foreground">Patient forms</div><div className="text-lg font-semibold">English &amp; Spanish</div></div>
                <a href="#pricing" className="rounded-lg bg-secondary p-3 hover:bg-secondary/70" data-testid="landing-pricing-jump"><div className="text-muted-foreground">Pricing</div><div className="text-lg font-semibold">Ways to pay ↓</div></a>
              </div>
            </div>
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-4 lg:px-6 pb-16 grid md:grid-cols-4 gap-4">
          {[
            { icon: ClipboardList, title: 'Patient → Medical team', desc: 'Patient fills a guided form via secure link. Your medical team adds clinical detail and uploads paper records.' },
            { icon: ScanLine, title: 'Employer form scanner', desc: "Patient photographs their employer's own HR form, or uploads the PDF. Every question is read and every answer captured — entered directly on fillable forms, and gathered on a clear companion sheet for anything that can't be placed on the original." },
            { icon: Stethoscope, title: 'AI → Doctor', desc: 'AI grounded in your procedure catalog drafts restrictions and recovery timelines — with all patient identifiers stripped before any AI call. Doctor edits and signs.' },
            { icon: ShieldCheck, title: 'Sign → Deliver', desc: 'Signed PDF delivered to the patient by secure link, ready to hand to their employer — or faxed direct where the practice enables it. Every action is audit-logged.' },
          ].map((c) => {
            const I = c.icon;
            return (
              <Card key={c.title} className="border bg-white">
                <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-lg"><I className="h-5 w-5 text-primary" /> {c.title}</CardTitle></CardHeader>
                <CardContent className="pt-0"><p className="text-base text-muted-foreground">{c.desc}</p></CardContent>
              </Card>
            );
          })}
        </section>

        <section id="pricing" className="max-w-6xl mx-auto px-4 lg:px-6 pb-16 scroll-mt-20" data-testid="landing-pricing">
          <h2 className="text-2xl lg:text-3xl font-semibold tracking-tight">Three ways to pay</h2>
          <p className="mt-2 text-lg text-muted-foreground max-w-3xl">
            Every way ends in the same finished package: the paperwork completed, signed by the doctor and delivered.
            What changes is who pays for each document.
          </p>
          <div className="mt-6 grid gap-4 lg:grid-cols-3">
            <PricingCard icon={Users} tag="Your practice · Standard" title="Your practice pays per document" testid="pricing-practice-pays"
              who="How most practices start. Good if you would rather not charge patients, or want to set your own fee.">
              <p>
                The practice pays a monthly subscription{planC && <> of <strong>{usd(planC.site_monthly)} per site</strong>, with {planC.included_submissions} documents a month included</>}.
              </p>
              <p>
                {ladder.length > 1
                  ? <>Each document after that is billed to the practice at the end of the month: <strong>{usd(ladder[0].price)}</strong> each, falling to <strong>{usd(ladder[ladder.length - 1].price)}</strong> as your volume grows.</>
                  : 'Each document after that is billed to the practice at the end of the month, for less per document as your volume grows.'}
              </p>
              <p className="text-muted-foreground">We charge your patients nothing. If you charge them a fee of your own, you keep all of it.</p>
            </PricingCard>
            <PricingCard icon={Building2} tag="Your practice · Available on request" title="Your patients pay per document" testid="pricing-patient-pays"
              who="For practices that want the paperwork to cost the practice as little as possible.">
              <p>
                The practice pays a monthly subscription{mode1 && <> of <strong>{usd(mode1.base_monthly)}/month</strong>, with {mode1.included_seats.doctor} doctor, {mode1.included_seats.admin} admin and {mode1.included_seats.staff} staff logins included</>}.
              </p>
              <p>
                Each patient pays a per-document fee online, before the paperwork is prepared. The practice never handles the payment.
              </p>
              <p className="text-muted-foreground">Ask us when you sign up and we will set your practice up this way.</p>
            </PricingCard>
            <PricingCard icon={Send} tag={directOn ? 'Patients' : 'Patients · Coming soon'} title="Patient-direct requests" testid="pricing-patient-direct"
              who="For a patient whose doctor isn't on DoctorPaperwork yet.">
              <p>
                The patient starts on their own, pays a one-time fee{directOn && direct?.fee != null && <> of <strong>{usd(direct.fee)}</strong></>}, and we email the request to their doctor's office. The card is only charged if the office accepts.
              </p>
              <p><strong>Free for the practice.</strong> The office signs up, accepts the request, and the case runs like any other.</p>
              {directOn
                ? <Button variant="outline" className="self-start mt-auto" onClick={() => navigate('/start')} data-testid="pricing-patient-direct-start">Start a request <ArrowRight className="h-4 w-4 ml-1" /></Button>
                : <p className="mt-auto text-muted-foreground font-medium" data-testid="pricing-patient-direct-soon">Coming soon.</p>}
            </PricingCard>
          </div>
        </section>

        {directOn && (
          <section className="max-w-6xl mx-auto px-4 lg:px-6 pb-16" data-testid="landing-patient-cta">
            <div className="rounded-2xl border bg-accent/40 p-6 lg:p-8">
              <h2 className="text-2xl font-semibold tracking-tight">How it works for patients</h2>
              <ol className="mt-4 grid md:grid-cols-3 gap-4 text-base">
                <li><span className="font-semibold">1. Start your request.</span> Your name, how to reach you, and your doctor's office email. Nothing about your health yet.</li>
                <li><span className="font-semibold">2. Pay once.</span> We hold a single fee{direct?.fee != null && <> of {usd(direct.fee)}</>} on your card. You are only charged if your doctor's office accepts.</li>
                <li><span className="font-semibold">3. Fill in your forms.</span> Once the office accepts, a secure link takes you to your paperwork. The doctor signs, and you collect the finished package.</li>
              </ol>
              <Button size="lg" className="mt-6" onClick={() => navigate('/start')} data-testid="landing-cta-start">
                Start a request <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </section>
        )}

        <section className="max-w-6xl mx-auto px-4 lg:px-6 pb-24">
          <div className="rounded-2xl border bg-white p-6 lg:p-8 grid lg:grid-cols-3 gap-6 items-center">
            <div className="lg:col-span-2">
              <h2 className="text-xl lg:text-2xl font-semibold tracking-tight">Packet coverage on day one</h2>
              <p className="mt-1 text-base text-muted-foreground">FMLA · FMLA + STD · FMLA + STD + LTD · ADA — with Functional Capacity, due-date tracking, and multi-block provider signatures built in.</p>
              <ul className="mt-4 grid sm:grid-cols-2 gap-2 text-base">
                {['Employer HR-form scanner + addendum','De-identified AI (no PHI to the model)','Procedure catalog (manual + CSV import)','Functional Capacity matrix','Multi-section signed PDFs','Email / fax / patient-collected delivery','Practice self-onboarding + team invites','Practice admin + billing portal'].map(x => (
                  <li key={x} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-primary" /> {x}</li>
                ))}
              </ul>
            </div>
            <div className="flex flex-col gap-2">
              <Button onClick={() => navigate('/signup')} data-testid="landing-secondary-signup">Get started — it's self-serve</Button>
              {demoMode ? (
                <Button variant="outline" onClick={() => goAs('admin', '/admin')} data-testid="landing-secondary-admin">Open the interactive demo</Button>
              ) : (
                <Button variant="outline" onClick={() => navigate('/login')} data-testid="landing-secondary-signin">Sign in to your practice</Button>
              )}
            </div>
          </div>
        </section>

        <footer className="border-t py-6 text-sm text-muted-foreground">
          <div className="max-w-6xl mx-auto px-4 lg:px-6 flex flex-wrap items-center gap-3 justify-between">
            <div>© {new Date().getFullYear()} {LEGAL_ENTITY} · DoctorPaperwork.{demoMode ? ' Demo build. PHI is synthetic.' : ''}</div>
            <div className="flex flex-wrap gap-3">
              <Link to="/terms" className="hover:text-foreground" data-testid="footer-terms">Terms</Link>
              <Link to="/privacy" className="hover:text-foreground" data-testid="footer-privacy">Privacy</Link>
              <Link to="/hipaa" className="hover:text-foreground" data-testid="footer-hipaa">HIPAA</Link>
              <Link to="/baa" className="hover:text-foreground" data-testid="footer-baa">BAA</Link>
              <span aria-hidden>·</span>
              <span>HIPAA-aligned</span> · <span>BAA-ready</span> · <span>Audit logged</span>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
